import { ICalendarHeader, ICalendarItemData } from 'Calendar/types';
import React, { memo, useMemo } from 'react';
import styled from 'styled-components';
import Appointment from './Appointment';
import BlockHourItem from './BlockHour';
import MultiAppointment, { compactCardCss } from './MultiAppointment';
import { CellRange, computeSlotPosition, sortByStart } from './multi-layout';

type Props = {
  blockItems: ICalendarItemData[];
  appointments: ICalendarItemData[];
  /** Time window the merged cell covers, from the row grid. */
  cellRange: CellRange;
  headerData?: ICalendarHeader;
  isMonthView?: boolean;
  disabled?: boolean;
};

/**
 * A merged cell holding both block-hours and appointments, rendered as two tracks inside the single
 * `<td>` the row already owns: blocks on the left half, appointments on the right. Each item sits at
 * its own time offset within the cell, so a short block no longer hides the appointments behind it
 * (the previous rendering showed the first block only, with the rest under a "!" popover).
 *
 * The table keeps one cell per column — no colspan — so column alignment and drop targets are
 * untouched.
 */
const SplitBlockColumn: React.FC<Props> = ({ blockItems, appointments, cellRange, headerData, isMonthView, disabled }) => {
  const blockSlots = useMemo(
    () => sortByStart(blockItems).map(item => ({ item, ...computeSlotPosition(item, cellRange) })),
    [blockItems, cellRange],
  );

  const aptSlot = useMemo(() => {
    if (appointments.length !== 1) return null;
    const item = appointments[0];
    return { item, ...computeSlotPosition(item, cellRange) };
  }, [appointments, cellRange]);

  return (
    <SplitStyled>
      <div className="block-track">
        {blockSlots.map(({ item, top, height }) => (
          <div key={item.id} className="slot" style={{ top: `${top}%`, height: `${height}%` }}>
            <BlockHourItem
              data={item}
              headerData={headerData}
              isMonthView={isMonthView}
              disabled={disabled}
            />
          </div>
        ))}
      </div>
      <div className="apt-track">
        {aptSlot ? (
          <div className="slot" style={{ top: `${aptSlot.top}%`, height: `${aptSlot.height}%` }}>
            <Appointment
              data={aptSlot.item}
              headerData={headerData}
              isMonthView={isMonthView}
              disabled={disabled}
            />
          </div>
        ) : (
          // Several appointments share the track: the grid shows them all side by side, where
          // stacking them by time would just hide the later ones behind the earlier ones.
          <div className="track-fill">
            <MultiAppointment
              split
              cellRange={cellRange}
              data={appointments}
              headerData={headerData}
              isMonthView={isMonthView}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </SplitStyled>
  );
};

export default memo(SplitBlockColumn);

const SplitStyled = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  gap: 2px;

  .block-track {
    flex: 0 0 50%;
    height: 100%;
    position: relative;
  }
  .apt-track {
    flex: 1;
    height: 100%;
    position: relative;
  }
  .slot {
    position: absolute;
    left: 0;
    right: 0;
    overflow: hidden;
    ${compactCardCss}
  }
  .track-fill {
    position: absolute;
    inset: 0;
  }
`;
